/* eslint-disable max-lines -- cohesive agents DAL singleton (one class ⇒ one file); splitting the service
   object would fragment the API surface. Mirrors the same allowance on orioniq-be's orion-iq.routes.ts (Artemis). */
import { AiChatRoles } from '@logz-pkg/models';
import type { IIntegrationManifestIndexEntry } from '@logz-pkg/enums';
import { ISearchRequestObject } from '@logz-build/typescript';
import { CrudApiService } from '../utilities/crud.api.service';
import { httpService } from '../../core/http/request.provider';
import type {
  IAgent,
  IAgentRun,
  IAgentsResponse,
  IAgentRunsResponse,
  IAgentRunSearchFilter,
  IAgentSearchFilter,
  ICreateAgent,
  ICreateAgentRun,
  UsagePerformanceResponse,
  IAgentBuilderRequest,
  IAgentBuilderResponse,
  IAgentCommonTranscribeResponse,
  IMarketplaceTemplateDisplay,
  IMarketplaceTemplatePreview,
  IRcaLifecycleResult,
  IRcaAgentConfig,
  IRcaActivationRecommendation,
  IOiqIntegrationConnectionOptions,
  IAgentAccountOption,
  RcaAgentVersion,
} from './types';
import type {
  IAgentRunSessionResponse,
  IGeneralAgentChatRequest,
  IGeneralAgentChatPostResponse,
  IConversationListResponse,
  IConversationTranscriptResponse,
  ILegacyConversationListResponse,
  ILegacyConversationTranscriptResponse,
  IChatModesResponse,
  IUserPreferences,
  IUserPreferencesPatch,
} from './general-agent.types';

type ManualTriggerResponse = { status: string; sessionId?: string; tabSessionId?: string };

type ReinvokeResponse = { sessionId: string; status: string };

export type LogzioSecurityEntry = {
  requestedAccountId?: number;
  type?: 'log' | 'metric';
  provider?: string;
  filter?: Record<string, unknown> | null;
};

export type ExternalSecurityEntry = {
  filter?: Record<string, unknown>;
  type?: string;
  provider?: string;
};

export type SecurityEntry = LogzioSecurityEntry | ExternalSecurityEntry;

export type ManualTriggerContextPayload = {
  message?: string;
  security?: SecurityEntry[];
  additionalContext?: string;
  [key: string]: unknown;
};

class AgentsApiService {
  private crudApiService = new CrudApiService<IAgent>();
  private crudRunApiService = new CrudApiService<IAgentRun>();

  private urls = {
    agents: '/orioniq-be/agents',
    agentRuns: '/orioniq-be/agent-runs',
    usagePerformance: '/orioniq-be/usage-performance',
    initializeAgents: '/orioniq-be/agents/initialize',
    syncAlertAgent: '/orioniq-be/agents/sync-alert',
    ensureProducts: '/orioniq-be/agents/ensure-products',
    agentBuilder: '/orioniq-be/agents/builder',
    generalAgent: '/orioniq-be/agents/general-agent',
    stopGeneralAgent: '/orioniq-be/agents/general-agent/stop',
    oiqBot: '/orioniq-be/internal/ai-agent/General',
    conversations: '/orioniq-be/conversations',
    chatModes: '/orioniq-be/chat-modes',
    userPreferences: '/orioniq-be/user/preferences',
    conversation: (sessionId: string) => `/orioniq-be/conversations/${encodeURIComponent(sessionId)}`,
    legacyConversations: '/orioniq-be/conversations/legacy',
    legacyConversation: (convId: string) => `/orioniq-be/conversations/legacy/${encodeURIComponent(convId)}`,
    agentCommonTranscribe: '/orioniq-be/agents/builder/transcribe',
    triggerManualAgent: (agentId: string) => `/orioniq-be/internal/ai-agent/${agentId}`,
    reinvokeAgentRun: (runId: string) => `/orioniq-be/internal/agent-runs/${runId}/reinvoke`,
    agentByAlertId: (alertId: string) => `/orioniq-be/agents/alert/${alertId}`,
    rcaLifecycle: (alertId: string) => `/orioniq-be/agents/alert/${alertId}/rca`,
    marketplaceTemplates: '/orioniq-be/marketplace/templates',
    marketplaceTemplate: (id: string) => `/orioniq-be/marketplace/templates/${id}`,
    implementationHelp: '/orioniq-be/implementation-help',
    oiqConnectionOptions: '/orioniq-be/integrations/connection-options',
    oiqIntegrationsManifest: '/orioniq-be/integrations/manifest',
    logAccounts: '/orioniq-be/agents/log-accounts',
    metricsAccounts: '/orioniq-be/agents/metrics-accounts',
  };

  ensureProducts = async (): Promise<void> => {
    await httpService.post(this.urls.ensureProducts);
  };

  getAgentNames = async (accountIds: number[] = []): Promise<string[]> => {
    const query =
      accountIds.length > 0 ? `?${accountIds.map(id => `accountIds=${encodeURIComponent(id)}`).join('&')}` : '';
    const response = await httpService.get<string[]>(`${this.urls.agents}/names${query}`);

    return response?.data ?? [];
  };

  searchAgents = async (requestObject: ISearchRequestObject<IAgentSearchFilter>): Promise<IAgentsResponse> => {
    return this.crudApiService.search(`${this.urls.agents}/search`, requestObject);
  };

  getAgent = async (id: string): Promise<IAgent> => {
    return this.crudApiService.get(`${this.urls.agents}/${id}`);
  };

  createAgent = async (agent: ICreateAgent): Promise<IAgent> => {
    return this.crudApiService.create(this.urls.agents, agent as IAgent);
  };

  updateAgent = async (agent: Partial<IAgent> & { id: string }): Promise<IAgent> => {
    return this.crudApiService.update(`${this.urls.agents}/${agent.id}`, agent as IAgent);
  };

  deleteAgent = async (id: string): Promise<IAgent> => {
    return this.crudApiService.delete(`${this.urls.agents}/${id}`);
  };

  initializeAgents = async (): Promise<void> => {
    await httpService.post(this.urls.initializeAgents);
  };

  syncAlertAgent = async (params: {
    alertId: string;
    alertName?: string;
    rcaEnabled: boolean;
    runbook?: string;
    notificationEndpointIds?: number[];
    emails?: string[];
    dailyInvocationLimit?: number | null;
    softDailyInvocationLimit?: number | null;
    active?: boolean;
  }): Promise<IAgent | null> => {
    const response = await httpService.post<IAgent | null>(this.urls.syncAlertAgent, params);

    return response.data;
  };

  syncRcaLifecycle = async (params: {
    alertId: string;
    enabled: boolean;
    isLogAlert: boolean;
    alertName?: string;
    runbook?: string | null;
    deliveryEndpointIds?: number[];
    deliveryEmails?: string[];
    dailyInvocationLimit?: number | null;
    softDailyInvocationLimit?: number | null;
    active?: boolean;
    rcaVersion?: RcaAgentVersion;
    agentConfig?: IRcaAgentConfig;
    activate?: boolean;
    recommendation?: IRcaActivationRecommendation;
  }): Promise<IRcaLifecycleResult> => {
    const { alertId, ...body } = params;
    const response = await httpService.put<IRcaLifecycleResult>(this.urls.rcaLifecycle(alertId), body);

    return response.data;
  };

  // For a deleted alert or rule. Turning RCA off (`syncRcaLifecycle` with `enabled: false`) only switches the agent off.
  removeRcaAgents = async (alertId: string): Promise<IRcaLifecycleResult> => {
    const response = await httpService.delete<IRcaLifecycleResult>(this.urls.rcaLifecycle(alertId));

    return response.data;
  };

  getAgentByAlertId = async (alertId: string, { shouldThrow = false } = {}): Promise<IAgent | null> => {
    const request = httpService.get<IAgent>(this.urls.agentByAlertId(alertId));
    const response = shouldThrow ? await request : await request.catch(() => null);

    return response?.data ?? null;
  };

  getOiqConnectionOptions = async (): Promise<IOiqIntegrationConnectionOptions[]> => {
    const response = await httpService.get<IOiqIntegrationConnectionOptions[]>(this.urls.oiqConnectionOptions);

    return response.data;
  };

  /** OrionIQ's integrations catalog: every integration's name and icon, whether or not the account connected it. */
  getOiqIntegrationsManifest = async (): Promise<IIntegrationManifestIndexEntry[]> => {
    const response = await httpService.get<IIntegrationManifestIndexEntry[]>(this.urls.oiqIntegrationsManifest);

    return response.data;
  };

  getLogAccounts = async (): Promise<IAgentAccountOption[]> => {
    const response = await httpService.get<IAgentAccountOption[]>(this.urls.logAccounts);

    return response.data;
  };

  getMetricsAccounts = async (): Promise<IAgentAccountOption[]> => {
    const response = await httpService.get<IAgentAccountOption[]>(this.urls.metricsAccounts);

    return response.data;
  };

  searchAgentRuns = async (requestObject: ISearchRequestObject<IAgentRunSearchFilter>): Promise<IAgentRunsResponse> => {
    return this.crudRunApiService.search(`${this.urls.agentRuns}/search`, requestObject);
  };

  submitFeedback = async ({
    agentId,
    sessionId,
    score,
    comment,
  }: {
    agentId: string;
    sessionId: string;
    score: number;
    comment?: string;
  }): Promise<unknown> => {
    const response = await httpService.post(`${this.urls.agentRuns}/${agentId}/feedback`, {
      sessionId,
      score,
      comment,
    });

    return response.data;
  };

  getAgentRun = async (id: string): Promise<IAgentRun> => {
    return this.crudRunApiService.get(`${this.urls.agentRuns}/${id}`);
  };

  getAgentRunBySessionId = async (sessionId: string): Promise<IAgentRunSessionResponse> => {
    const response = await httpService.get<IAgentRunSessionResponse>(
      `${this.urls.agentRuns}/session/${encodeURIComponent(sessionId)}`,
    );

    return response.data;
  };

  createAgentRun = async (run: ICreateAgentRun): Promise<IAgentRun> => {
    return this.crudRunApiService.create(this.urls.agentRuns, run as IAgentRun);
  };

  updateAgentRun = async (run: Partial<IAgentRun> & { id: string }): Promise<IAgentRun> => {
    return this.crudRunApiService.update(`${this.urls.agentRuns}/${run.id}`, run as IAgentRun);
  };

  deleteAgentRun = async (id: string): Promise<IAgentRun> => {
    return this.crudRunApiService.delete(`${this.urls.agentRuns}/${id}`);
  };

  getUsagePerformance = async (days = 30): Promise<UsagePerformanceResponse> => {
    const response = await httpService.get<UsagePerformanceResponse>(this.urls.usagePerformance, { days });

    return response.data;
  };

  agentBuilderChat = async (params: IAgentBuilderRequest): Promise<IAgentBuilderResponse> => {
    const response = await httpService.post<IAgentBuilderResponse>(this.urls.agentBuilder, params);

    return response.data;
  };

  getGeneralAgent = async (): Promise<Record<string, unknown> | null> =>
    (await httpService.get<Record<string, unknown> | null>(this.urls.generalAgent)).data;

  generalAgentChat = async (
    params: IGeneralAgentChatRequest,
    options?: { signal?: AbortSignal },
  ): Promise<IGeneralAgentChatPostResponse> => {
    const response = await httpService.post<IGeneralAgentChatPostResponse>(
      this.urls.oiqBot,
      params,
      undefined,
      {},
      options?.signal ? { signal: options.signal } : {},
    );

    return response.data;
  };

  // Stops the in-progress general-agent (IQon) run; paired with generalAgentChat (one start/stop DAL). orioniq-be
  // builds the principal from its authenticated request context, so only the sessionId is sent.
  stopGeneralAgentRun = async (sessionId: string): Promise<void> => {
    await httpService.post(this.urls.stopGeneralAgent, { sessionId });
  };

  // The chat-mode catalog (ORIONIQ-1475). Cacheable and identical for everyone — the caller holds it in
  // React Query rather than this service caching it, since it only changes on an orioniq-be deploy.
  listChatModes = async (): Promise<IChatModesResponse> =>
    (await httpService.get<IChatModesResponse>(this.urls.chatModes)).data;

  // Per-user OrionIQ preferences (ORIONIQ-1463), shared with the mobile app and the OrionIQ standalone app.
  // Replaces app-api's app-state KV for `chatMode`, which is what makes the mode follow a user across surfaces.
  getUserPreferences = async (): Promise<IUserPreferences> =>
    (await httpService.get<IUserPreferences>(this.urls.userPreferences)).data;

  // PUT, but MERGE: orioniq-be writes only the keys the patch carries. So a client sending `{ chatMode }` cannot
  // clobber the notification toggles or starred agents it knows nothing about, and two concurrent writes
  // cannot race through a read-modify-write. Returns the merged document, which is authoritative.
  updateUserPreferences = async (patch: IUserPreferencesPatch): Promise<IUserPreferences> =>
    (await httpService.put<IUserPreferences>(this.urls.userPreferences, patch)).data;

  // Session-grouped chat history (ORIONIQ-1053). A conversation id IS a general-agent sessionId; the orioniq-be
  // `/conversations` collection is user-scoped. v1: list returns all within retention (client-side search).
  listConversations = async (): Promise<IConversationListResponse> =>
    (await httpService.get<IConversationListResponse>(this.urls.conversations)).data;

  // The full multi-run transcript for one session — the FE adapter merges the runs into one ChatMessage[].
  getConversation = async (sessionId: string): Promise<IConversationTranscriptResponse> =>
    (await httpService.get<IConversationTranscriptResponse>(this.urls.conversation(sessionId))).data;

  deleteConversation = async (sessionId: string): Promise<void> => {
    await httpService.delete(this.urls.conversation(sessionId));
  };

  // Set a conversation's title (ORIONIQ-1285). Server-side this retitles EVERY run of the session — a
  // conversation is N `oiq_agents_history` rows sharing a sessionId, each carrying its own copy of the title —
  // and it deliberately bypasses the auto-naming latch, so a user's name wins over the model's. It also stops
  // the model retitling later, because that latch is 'the title is no longer the default'.
  renameConversation = async (sessionId: string, title: string): Promise<void> => {
    await httpService.patch(this.urls.conversation(sessionId), { title });
  };

  // The LEGACY AI Assistant history (ORIONIQ-1307) — read-only, its own user-scoped collection. No delete: legacy
  // rows are a draining archive the retention job owns.
  listLegacyConversations = async (): Promise<ILegacyConversationListResponse> =>
    (await httpService.get<ILegacyConversationListResponse>(this.urls.legacyConversations)).data;

  getLegacyConversation = async (convId: string): Promise<ILegacyConversationTranscriptResponse> =>
    (await httpService.get<ILegacyConversationTranscriptResponse>(this.urls.legacyConversation(convId))).data;

  agentCommonTranscribe = async (audioFile: File): Promise<IAgentCommonTranscribeResponse> => {
    const formData = new FormData();

    formData.append('file', audioFile);

    const response = await httpService.post<IAgentCommonTranscribeResponse>(this.urls.agentCommonTranscribe, formData);

    return response.data;
  };

  getMarketplaceTemplates = async (): Promise<IMarketplaceTemplateDisplay[]> => {
    const response = await httpService.get<IMarketplaceTemplateDisplay[]>(this.urls.marketplaceTemplates);

    return response.data;
  };

  getMarketplaceTemplateById = async (id: string): Promise<IMarketplaceTemplatePreview> => {
    const response = await httpService.get<IMarketplaceTemplatePreview>(this.urls.marketplaceTemplate(id));

    return response.data;
  };

  requestImplementationHelp = async (pageUrl: string): Promise<void> => {
    await httpService.post(this.urls.implementationHelp, { pageUrl });
  };

  reinvokeAgentRun = async (runId: string): Promise<ReinvokeResponse> => {
    const response = await httpService.post<ReinvokeResponse>(this.urls.reinvokeAgentRun(runId));

    return response.data;
  };

  triggerManualAgent = async (
    agentId: string,
    contextPayload?: ManualTriggerContextPayload,
  ): Promise<ManualTriggerResponse> => {
    const userMessage = contextPayload?.message ?? 'Manual trigger';
    const body: Record<string, unknown> = {
      message: { role: AiChatRoles.User, content: userMessage },
    };

    if (contextPayload && Object.keys(contextPayload).length > 0) {
      body.context = contextPayload;
    }

    const response = await httpService.post<ManualTriggerResponse>(this.urls.triggerManualAgent(agentId), body);

    return response.data;
  };
}

export const agentsApiService = new AgentsApiService();
